import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Users } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, Badge, StatCard, EmptyState, Field } from '../ui';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  rnc: string;
  contacto: string;
  telefono: string;
  email: string;
  direccion: string;
  ubicacion: string;
  tipo: 'Industria' | 'Comercio' | 'Residencial' | 'Otro';
  capacidadTanque: number;
  estado: 'activo' | 'inactivo';
  observaciones: string;
  fecha_registro: string;
  ultima_compra?: string;
  dias_sin_comprar?: number;
}
interface ClientesProps {
  clientes: Cliente[];
  onAddCliente: (cliente: Cliente) => void;
  onDeleteCliente: (id: string) => void;
  onUpdateCliente: (cliente: Cliente) => void;
}
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
export function Clientes({
  clientes,
  onAddCliente,
  onDeleteCliente,
  onUpdateCliente
}: ClientesProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'activo' | 'inactivo'>('todos');
  const [formData, setFormData] = useState<Partial<Cliente>>({
    tipo: 'Comercio',
    estado: 'activo',
    capacidadTanque: 1000
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.codigo || !formData.nombre || !formData.email) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    if (editingId) {
      onUpdateCliente({
        ...formData,
        id: editingId
      } as Cliente);
      setEditingId(null);
    } else {
      onAddCliente({
        ...formData,
        id: Date.now().toString(),
        fecha_registro: new Date().toISOString().split('T')[0]
      } as Cliente);
    }
    setFormData({
      tipo: 'Comercio',
      estado: 'activo',
      capacidadTanque: 1000
    });
    setShowForm(false);
  };
  const handleEdit = (cliente: Cliente) => {
    setFormData(cliente);
    setEditingId(cliente.id);
    setShowForm(true);
  };
  const filteredClientes = clientes.filter(c => {
    const matchSearch = c.codigo.toLowerCase().includes(searchTerm.toLowerCase()) || c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todos' || c.estado === filterEstado;
    return matchSearch && matchEstado;
  });
  const clientesActivos = clientes.filter(c => c.estado === 'activo').length;
  const clientesInactivos = clientes.filter(c => c.estado === 'inactivo').length;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-152">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-153">
        
        <StatCard label="Total clientes" value={String(clientes.length)} note="Registrados en el sistema" accent={T.info} data-id="element-154" />
        
        <StatCard label="Activos" value={String(clientesActivos)} note="Comprando actualmente" accent={T.ok} data-id="element-155" />
        
        <StatCard label="Inactivos" value={String(clientesInactivos)} note="Sin actividad reciente" accent={T.danger} data-id="element-156" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-157">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-158">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-159">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-160" />
            
            <input type="text" placeholder="Buscar por código, nombre o email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-161" />
            
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-162">
            
            <option value="todos" data-id="element-163">Todos los estados</option>
            <option value="activo" data-id="element-164">Activos</option>
            <option value="inactivo" data-id="element-165">Inactivos</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            tipo: 'Comercio',
            estado: 'activo',
            capacidadTanque: 1000
          });
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-166">
            
            <Plus size={15} data-id="element-167" /> Nuevo Cliente
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-168">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-169">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-170">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-171">
              
                {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-172">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-173">
                
                  <Field label="Código *" data-id="element-174">
                    <input type="text" value={formData.codigo || ''} onChange={e => setFormData({
                  ...formData,
                  codigo: e.target.value
                })} style={inputStyle} placeholder="CLI-001" data-id="element-175" />
                  
                  </Field>
                  <Field label="Nombre de empresa *" data-id="element-176">
                    <input type="text" value={formData.nombre || ''} onChange={e => setFormData({
                  ...formData,
                  nombre: e.target.value
                })} style={inputStyle} placeholder="Nombre empresa" data-id="element-177" />
                  
                  </Field>
                  <Field label="RNC" data-id="element-178">
                    <input type="text" value={formData.rnc || ''} onChange={e => setFormData({
                  ...formData,
                  rnc: e.target.value
                })} style={inputStyle} placeholder="123456789" data-id="element-179" />
                  
                  </Field>
                  <Field label="Tipo de cliente" data-id="element-180">
                    <select value={formData.tipo || 'Comercio'} onChange={e => setFormData({
                  ...formData,
                  tipo: e.target.value as any
                })} style={inputStyle} data-id="element-181">
                    
                      <option data-id="element-182">Industria</option>
                      <option data-id="element-183">Comercio</option>
                      <option data-id="element-184">Residencial</option>
                      <option data-id="element-185">Otro</option>
                    </select>
                  </Field>
                  <Field label="Persona de contacto" data-id="element-186">
                    <input type="text" value={formData.contacto || ''} onChange={e => setFormData({
                  ...formData,
                  contacto: e.target.value
                })} style={inputStyle} placeholder="Nombre contacto" data-id="element-187" />
                  
                  </Field>
                  <Field label="Teléfono" data-id="element-188">
                    <input type="tel" value={formData.telefono || ''} onChange={e => setFormData({
                  ...formData,
                  telefono: e.target.value
                })} style={inputStyle} placeholder="+1-809-..." data-id="element-189" />
                  
                  </Field>
                  <Field label="Email *" data-id="element-190">
                    <input type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} style={inputStyle} placeholder="empresa@email.com" data-id="element-191" />
                  
                  </Field>
                  <Field label="Ubicación" data-id="element-192">
                    <input type="text" value={formData.ubicacion || ''} onChange={e => setFormData({
                  ...formData,
                  ubicacion: e.target.value
                })} style={inputStyle} placeholder="Zona/Región" data-id="element-193" />
                  
                  </Field>
                  <Field label="Capacidad tanque (galones)" data-id="element-194">
                    <input type="number" value={formData.capacidadTanque || 1000} onChange={e => setFormData({
                  ...formData,
                  capacidadTanque: parseInt(e.target.value)
                })} style={inputStyle} placeholder="1000" data-id="element-195" />
                  
                  </Field>
                  <Field label="Estado" data-id="element-196">
                    <select value={formData.estado || 'activo'} onChange={e => setFormData({
                  ...formData,
                  estado: e.target.value as any
                })} style={inputStyle} data-id="element-197">
                    
                      <option value="activo" data-id="element-198">Activo</option>
                      <option value="inactivo" data-id="element-199">Inactivo</option>
                    </select>
                  </Field>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-200">
                    <Field label="Dirección" data-id="element-201">
                      <input type="text" value={formData.direccion || ''} onChange={e => setFormData({
                    ...formData,
                    direccion: e.target.value
                  })} style={inputStyle} placeholder="Dirección completa" data-id="element-202" />
                    
                    </Field>
                  </div>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-203">
                    <Field label="Observaciones" data-id="element-204">
                      <textarea value={formData.observaciones || ''} onChange={e => setFormData({
                    ...formData,
                    observaciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={3} placeholder="Notas adicionales..." data-id="element-205" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-206">
                  <button type="submit" style={primaryButton} data-id="element-207">
                    {editingId ? 'Actualizar' : 'Guardar'} cliente
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-208">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lista de Clientes */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-209">
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-210">
          
          Clientes ({filteredClientes.length})
        </h3>
        {filteredClientes.length === 0 ? <EmptyState icon={<Users size={20} data-id="element-212" />} title="No hay clientes" hint="Registra un cliente nuevo o ajusta los filtros de búsqueda." data-id="element-211" /> : <div style={{
        overflowX: 'auto'
      }} data-id="element-213">
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-214">
              <thead data-id="element-215">
                <tr data-id="element-216">
                  {['Código', 'Empresa', 'Tipo', 'Contacto', 'Teléfono', 'Tanque (gal)', 'Estado', 'Acciones'].map((h, i) => <th key={h} style={{
                textAlign: i === 7 ? 'center' : 'left',
                padding: '0 10px 10px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: T.textMuted,
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-217">
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-218">
                {filteredClientes.map(cliente => <tr key={cliente.id} data-id="element-219">
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                fontFamily: 'monospace',
                color: T.info,
                fontWeight: 700
              }} data-id="element-220">
                  
                      {cliente.codigo}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                fontWeight: 600,
                color: T.text
              }} data-id="element-221">
                  
                      {cliente.nombre}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-222">
                  
                      <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 9px',
                  borderRadius: 99,
                  background: T.surfaceSubtle,
                  color: T.textSecondary
                }} data-id="element-223">
                    
                        {cliente.tipo}
                      </span>
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.textSecondary
              }} data-id="element-224">
                  
                      {cliente.contacto}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.textSecondary
              }} data-id="element-225">
                  
                      {cliente.telefono}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                color: T.text
              }} data-id="element-226">
                  
                      {cliente.capacidadTanque}
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-227">
                  
                      <Badge label={cliente.estado} color={cliente.estado === 'activo' ? T.ok : T.danger} soft={cliente.estado === 'activo' ? T.okSoft : T.dangerSoft} data-id="element-228" />
                  
                    </td>
                    <td style={{
                padding: '12px 10px',
                borderBottom: `1px solid ${T.border}`,
                textAlign: 'center'
              }} data-id="element-229">
                  
                      <div style={{
                  display: 'inline-flex',
                  gap: 6,
                  justifyContent: 'center'
                }} data-id="element-230">
                    
                        <button onClick={() => handleEdit(cliente)} title="Editar" style={{
                    ...iconBtn,
                    color: T.info
                  }} data-id="element-231">
                      
                          <Edit2 size={14} data-id="element-232" />
                        </button>
                        <button onClick={() => onDeleteCliente(cliente.id)} title="Eliminar" style={{
                    ...iconBtn,
                    color: T.danger
                  }} data-id="element-233">
                      
                          <Trash2 size={14} data-id="element-234" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}